<template>
  <div class="container">
    <md-card class="dash-content">
      <div class="header-wrapper">
        <PageHeader
          :title="`Update Prices for ${category ? category.title : 'Category'}`"
        />
        <div class="loader">
          <Wave
            v-if="loading || fetching || updating"
            width="50px"
            color="#d9534f"
          />
        </div>
        <div>
          <md-button class="md-primary" :to="{ name: 'Product Categories' }">
            Back to Categories
          </md-button>
        </div>
      </div>

      <div class="p-4">
        <div class="md-layout md-gutter">
          <div class="md-layout-item md-size-50">
            <md-autocomplete
              v-if="metadata"
              v-model="selectedState"
              :md-options="states"
            >
              <label>Search Location (State)</label>
              <template slot="md-autocomplete-empty" slot-scope="{ term }">
                No state matching "{{ term }}"
              </template>
            </md-autocomplete>
          </div>
        </div>

        <div v-if="selectedState && !fetching">
          <div class="mt-4">
            <h3>Total Products Found: {{ filteredProducts.length }}</h3>

            <md-button
              @click="showProducts = !showProducts"
              class="md-dense md-raised md-primary"
            >
              {{ showProducts ? "Hide Products" : "Show Products to Deselect" }}
            </md-button>

            <div v-if="showProducts" class="product-list mt-2">
              <div>
                <div
                  v-for="product in filteredProducts"
                  :key="product.id"
                  class="d-flex align-items-center px-3 py-2 mb-1"
                  :class="{
                    'selected': selectedProductIds.includes(product.id),
                  }"
                >
                  <md-checkbox
                    v-model="selectedProductIds"
                    :value="product.id"
                    :id="'product' + product.id"
                  />
                  <div >
                    <label class="d-flex align-items-center gap-3" :for="'product' + product.id">
                      <div class="image-wrapper">
                        <img
                          v-if="product.images.length"
                          :src="product.images[0]"
                          alt=""
                          class=""
                        />
                      </div>
                    <div>
                        <h4 class="md-list-item-text ">{{ product.title }}</h4>
                        <span class="md-list-item-text text-right"
                        >Current Price: {{ product.price | formatMoney }}</span
                      >
                    </div>
                    </label>
                  </div>
              </div>
            </div>
            </div>
          </div>

          <div class="update-form mt-8">
            <div class="md-layout md-gutter">
              <div class="md-layout-item md-size-50">
                <md-field>
                  <label>New Price</label>
                  <md-input
                    v-model="newPrice"
                    type="number"
                    required
                  ></md-input>
                </md-field>
              </div>
              
            </div>

            <div class="mt-4">
              <md-button
                class="md-raised md-primary"
                @click="submitUpdate"
                :disabled="
                  updating || selectedProductIds.length === 0 || !newPrice
                "
              >
                Update All Selected Prices ({{ selectedProductIds.length }})
              </md-button>
            </div>
          </div>
        </div>
      </div>
    </md-card>
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
const { mapActions, mapGetters } = createNamespacedHelpers("product");

export default {
  name: "UpdateCategoryPrice",
  data() {
    return {
      category: null,
      metadata: null,
      selectedState: "",
      filteredProducts: [],
      selectedProductIds: [],
      showProducts: false,
      newPrice: "",
      oldPrice: "",
      fetching: false,
      updating: false
    };
  },
  watch: {
    async selectedState(val) {
      if (
        val &&
        this.metadata &&
        this.metadata.states &&
        this.selectedStateKey
      ) {
        await this.fetchProducts();
      }
    }
  },
  methods: {
    ...mapActions(["getCat", "getMetaData", "getProductByStateAndCat", "bulkUpdatePrice"]),
    async fetchCategory() {
      try {
        const id = this.$route.params.id;
        this.category = await this.getCat(id);
      } catch (err) {
        this.toast("error", "Failed to fetch category");
      }
    },
    async fetchMetadata() {
      const res = await this.getMetaData();
      this.metadata = res.data;
    },
    async fetchProducts() {
      try {
        this.fetching = true;
        const params = {
          category_id: this.$route.params.id,
          state_id: this.selectedStateKey,
          per_page: 1000 // Get all products in category/state
        };
        const data = await this.getProductByStateAndCat(params);
        this.filteredProducts = data;

        console.log(data)
        // Select all by default
        this.selectedProductIds = data.map((p) => p.id);
        this.fetching = false;
      } catch (err) {
        console.log(err);
        this.fetching = false;
        this.toast("error", "Failed to fetch products");
      }
    },
    async submitUpdate() {
      try {
        const confirmUpdate = confirm(
          `Are you sure you want to update prices for ${this.selectedProductIds.length} products?`
        );
        if (!confirmUpdate) return;

        this.updating = true;
        const payload = {
          product_ids: this.selectedProductIds,
          price: this.newPrice,
        };
        await this.bulkUpdatePrice(payload);
        this.toast("success", "Prices updated successfully");
        this.updating = false;
        // Refresh products
        await this.fetchProducts();
      } catch (err) {
        this.updating = false;
        this.toast("error", "Failed to update prices");
      }
    }
  },
  async mounted() {
    await this.fetchCategory();
    await this.fetchMetadata();
  },
  computed: {
    ...mapGetters(["loading"]),
    states() {
      if (!this.metadata || !this.metadata.states) return [];
      return Object.values(this.metadata.states);
    },
    selectedStateKey() {
      if (!this.metadata || !this.metadata.states || !this.selectedState)
        return "";
      return (
        Object.keys(this.metadata.states).find(
          (key) => this.metadata.states[key] === this.selectedState
        ) || ""
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.p-4 {
  padding: 1.5rem;
}
.image-wrapper {
  width: 100px;
  height: 100px;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
.mt-2 {
  margin-top: 0.5rem;
}
.mt-4 {
  margin-top: 1rem;
}
.mt-8 {
  margin-top: 2rem;
}
.product-list {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 4px;
}
.text-right {
  text-align: right;
}

.selected{
    background: #fdefef !important;
}
</style>
